
import { User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { format } from "date-fns";
import { displayName } from "@/lib/utils";

export interface HostProfileSummary {
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  bio?: string | null;
  hostType: string;
  notionLink?: string;
  joinedDate?: string | null;
  hostId?: string;
}

interface HostBioTileProps {
  hosts: HostProfileSummary[];
}

const HostBioTile = ({ hosts }: HostBioTileProps) => {
  return (
    <div className="mt-6 p-4 bg-card rounded-lg shadow-sm border border-border">
      <h2 className="text-xl font-semibold mb-3 text-foreground font-nexa">
        {hosts.length > 1 ? "Your Hosts" : "Your Host"}
      </h2>
      <div className="space-y-5">
        {hosts.map((host, index) => {
          const fullName = displayName(host.firstName, host.lastName, "N/A");
          const formattedJoinDate = host.joinedDate
            ? format(new Date(host.joinedDate), "MMMM yyyy")
            : null;
          const notionLink = host.notionLink || "https://nimi-live.notion.site/meet-your-hosts";

          return (
            <div
              key={host.hostId || `${host.firstName}-${host.lastName}-${index}`}
              className="flex items-start gap-3"
            >
              <Avatar className="h-28 w-28 shrink-0 bg-muted">
                <AvatarImage
                  src={host.avatarUrl || ""}
                  alt={fullName}
                  className="object-center object-cover capitalize"
                />
                <AvatarFallback>
                  <User className="h-12 w-12 text-muted-foreground" />
                </AvatarFallback>
              </Avatar>

              <div className="min-w-0 flex-1">
                <div className="flex flex-col">
                  <h4 className="font-medium text-base">{fullName}</h4>
                  {formattedJoinDate && host.hostType === "member" && (
                    <span className="text-sm text-muted-foreground">
                      Member since {formattedJoinDate}
                    </span>
                  )}
                  {host.hostType === "artist" && (
                    <span className="text-sm text-muted-foreground">Artist Host</span>
                  )}
                </div>

                {host.bio && (
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{host.bio}</p>
                )}

                <a
                  href={notionLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 text-sm text-secondary hover:text-secondary/80 inline-flex items-center"
                >
                  Read more about {host.firstName} →
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HostBioTile;